import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Database,
  Phone,
  BarChart3,
  Check,
  ExternalLink,
  BookOpen,
  Presentation,
  UserPlus,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
} from 'lucide-react';
import SectionHeader from '../ui/SectionHeader';
import { auth } from '../../firebase';

const initialReferralForm = {
  referralEmail: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  comments: '',
  privacyPolicy: true,
  allowsComm: false,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CRMPage = ({ user }) => {
  const location = useLocation();
  const [formData, setFormData] = useState(() => ({
    ...initialReferralForm,
    referralEmail: user?.email || '',
  }));
  const [formStatus, setFormStatus] = useState('idle');
  const [formError, setFormError] = useState('');

  const isSubmitting = formStatus === 'submitting';

  useEffect(() => {
    if (!location.hash) return;

    const target = document.querySelector(location.hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const agentInitials = useMemo(() => {
    const source = user?.displayName || user?.email || 'Agente Sonneil';
    return source
      .split(/[.\s@_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }, [user]);

  const handleReferralChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateReferralForm = () => {
    if (!formData.referralEmail.trim() || !emailRegex.test(formData.referralEmail)) {
      return 'Indica un email válido del agente que prescribe el cliente.';
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      return 'Completa el nombre y apellidos del cliente.';
    }

    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      return 'Indica un email válido del cliente.';
    }

    if (formData.phone.trim().length < 6) {
      return 'Indica un teléfono válido del cliente.';
    }

    if (!formData.allowsComm) {
      return 'Debes confirmar la autorización de comunicaciones antes de enviar.';
    }

    return '';
  };

  const handleReferralSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateReferralForm();
    if (validationError) {
      setFormError(validationError);
      setFormStatus('error');
      return;
    }

    setFormError('');
    setFormStatus('submitting');

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/recommend-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...formData,
          referralEmail: formData.referralEmail.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          comments: formData.comments.trim(),
          language: 'ES',
          developmentSlug: 'RecommendClients',
          windowUrl: window.location.href,
          submittedBy: user?.email || formData.referralEmail.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('No se pudo enviar la prescripción.');
      }

      setFormData({
        ...initialReferralForm,
        referralEmail: user?.email || '',
      });
      setFormStatus('success');
    } catch (error) {
      setFormError(error.message || 'Ha ocurrido un error al enviar la prescripción.');
      setFormStatus('error');
    }
  };

  return (
    <div className="animate-stagger">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-2">CRM y Ventas</h1>
        <p className="text-text-secondary">
          Herramientas para gestión comercial, seguimiento de clientes y comunicaciones
        </p>
      </div>

      {/* Salesforce Section */}
      <section id="salesforce" className="section scroll-mt-24">
        <div className="card overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Database size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Salesforce CRM</h2>
                <p className="text-blue-100">CRM Corporativo de Sonneil</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-text-secondary mb-6 leading-relaxed">
              Nuestro CRM cuenta con un altísimo nivel de desarrollo y automatismo que permite 
              que nuestros agentes comerciales puedan obtener la información de manera rápida 
              y eficaz para suministrar a nuestros clientes.
            </p>

            <h4 className="font-semibold text-text-primary mb-3">Funcionalidades principales:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {[
                'Envío de emails automatizados',
                'Registro de clientes por landing pages',
                'Envío de SMS integrado',
                'Llamadas desde el sistema',
                'Seguimiento de oportunidades',
                'Dashboard de ventas',
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-text-secondary">
                  <Check size={16} className="text-success flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://sonneil.lightning.force.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Abrir Salesforce
                <ExternalLink size={16} />
              </a>
              <a
                href="https://drive.google.com/file/d/1W93QNyOx83v5otMYovYuUk4cQeYT1SzA/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <BookOpen size={16} />
                Manual Uso de SF Sales Agents
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Aircall Section */}
      <section id="aircall" className="section scroll-mt-24">
        <div className="card overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Phone size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Aircall</h2>
                <p className="text-green-100">Plataforma de Llamadas</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-text-secondary mb-6 leading-relaxed">
              Centralita de llamadas donde gestionamos las llamadas telefónicas de manera más eficiente, 
              distribuido en un flujo de llamadas entrantes y salientes a nivel nacional o geográficamente, 
              que nos permite que se vea reflejado un número local a los diferentes países a los que marcamos.
            </p>

            <h4 className="font-semibold text-text-primary mb-3">Características destacadas:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {[
                { title: 'Saludo de Bienvenida', desc: 'Mensajes personalizados' },
                { title: 'IVR Inteligente', desc: 'Selección múltiple de opciones' },
                { title: 'Zona Horaria', desc: 'Configuración automática' },
                { title: 'Enrutamiento', desc: 'Llamadas inteligentes' },
                { title: 'Buzón de Mensajes', desc: 'Gestión de voicemail' },
                { title: 'Números Locales', desc: 'Para cada país' },
              ].map((item, index) => (
                <div key={index} className="bg-background p-4 rounded-lg">
                  <h5 className="font-medium text-text-primary text-sm">{item.title}</h5>
                  <p className="text-xs text-text-tertiary mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://app.aircall.io"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Abrir Aircall
                <ExternalLink size={16} />
              </a>
              <a
                href="https://drive.google.com/file/d/1-Tk6qQ5IL9quvCmPVLLD2B6L4obBV0FC/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <BookOpen size={16} />
                Manual de Configuración
              </a>
              <a
                href="https://docs.google.com/presentation/d/e/2PACX-1vRF1pZ__zECYtjEqp1DnG3zIX8ioKiFNYduPWyoHW_2yGbSVAU7KKpex6P9LS70Dbi2xp-Frcqe2o5A/pubembed?start=false&loop=false&delayms=3000"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-tertiary"
              >
                <Presentation size={16} />
                Capacitación Aircall
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section id="analytics-comercial" className="section scroll-mt-24">
        <SectionHeader
          title="Analytics Comercial"
          description="Herramientas de análisis y métricas de ventas"
        />
        <div className="card p-6 border-2 border-dashed border-border">
          <div className="flex items-center gap-4 text-text-tertiary">
            <BarChart3 size={40} className="opacity-50" />
            <div>
              <h4 className="font-medium text-text-secondary">Próximamente</h4>
              <p className="text-sm">
                Dashboards de análisis comercial y métricas de rendimiento
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Referral Section */}
      <section id="prescribir-cliente" className="section scroll-mt-24">
        <div className="card overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <UserPlus size={32} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Prescribir cliente</h2>
                  <p className="text-white/80">
                    Recomienda clientes interesados en comprar una propiedad
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-2xl bg-white/15 px-4 py-3">
                <div className="w-10 h-10 rounded-full bg-white text-primary flex items-center justify-center font-semibold">
                  {agentInitials}
                </div>
                <div>
                  <p className="text-white/70 text-xs">Agente</p>
                  <p className="text-white font-medium text-sm">{user?.email || 'Sonneil'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-0">
            <div className="p-6 lg:p-8 bg-primary-lighter/20 border-b xl:border-b-0 xl:border-r border-primary-lighter/60">
              <h3 className="text-xl font-semibold text-text-primary mb-3">
                Recomienda clientes y gana recompensas exclusivas
              </h3>

              <div className="space-y-4">
                {[
                  'Cuando completen la compra, recibirás una bonificación exclusiva por tu recomendación.',
                  'Nosotros nos encargamos de todo: tú solo debes presentarles la oportunidad.',
                  'Tus clientes acceden a viviendas exclusivas, asesoramiento integral y opciones de financiación.',
                  'Sonneil aporta más de 10 años de experiencia y un equipo especializado en clientes internacionales.',
                ].map((condition) => (
                  <div key={condition} className="flex gap-3">
                    <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-sm text-text-secondary">{condition}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-xl bg-white border border-primary-lighter flex items-start gap-3">
                <ShieldCheck className="text-primary flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-text-secondary">
                  La prescripción queda registrada para el seguimiento comercial interno
                  y vinculada al agente autenticado en la intranet.
                </p>
              </div>
            </div>

            <form onSubmit={handleReferralSubmit} className="p-6 lg:p-8">
              <input
                type="hidden"
                name="referralEmail"
                value={formData.referralEmail}
                readOnly
              />
              <input type="hidden" name="privacyPolicy" value="1" readOnly />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label>
                  <span className="block text-sm font-medium text-text-primary mb-2">
                    Nombre del cliente
                  </span>
                  <input
                    className="input"
                    type="text"
                    name="firstName"
                    placeholder="Nombre"
                    value={formData.firstName}
                    onChange={handleReferralChange}
                    required
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-text-primary mb-2">
                    Apellidos del cliente
                  </span>
                  <input
                    className="input"
                    type="text"
                    name="lastName"
                    placeholder="Apellidos"
                    value={formData.lastName}
                    onChange={handleReferralChange}
                    required
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-text-primary mb-2">
                    Email del cliente
                  </span>
                  <input
                    className="input"
                    type="email"
                    name="email"
                    placeholder="cliente@email.com"
                    value={formData.email}
                    onChange={handleReferralChange}
                    required
                  />
                </label>

                <label>
                  <span className="block text-sm font-medium text-text-primary mb-2">
                    Teléfono del cliente
                  </span>
                  <input
                    className="input"
                    type="tel"
                    name="phone"
                    placeholder="+34 600 000 000"
                    value={formData.phone}
                    onChange={handleReferralChange}
                    required
                  />
                </label>

                <label className="md:col-span-2">
                  <span className="block text-sm font-medium text-text-primary mb-2">
                    Promoción, presupuesto, zona o características
                  </span>
                  <textarea
                    className="input min-h-[120px]"
                    name="comments"
                    placeholder="Añade cualquier información útil para el equipo comercial"
                    value={formData.comments}
                    onChange={handleReferralChange}
                  />
                </label>
              </div>

              <div className="mt-5">
                <label className="flex gap-3 text-sm text-text-secondary">
                  <input
                    className="mt-1 rounded border-border text-primary focus:ring-primary"
                    type="checkbox"
                    name="allowsComm"
                    checked={formData.allowsComm}
                    onChange={handleReferralChange}
                    required
                  />
                  <span>
                    Confirmo que el cliente consiente recibir comunicaciones por correo
                    electrónico y/o información telefónica acerca de productos y servicios
                    inmobiliarios.
                  </span>
                </label>
              </div>

              {formStatus === 'success' && (
                <div className="mt-5 p-4 rounded-xl bg-success/10 text-success flex items-center gap-3">
                  <CheckCircle2 size={20} />
                  <p className="text-sm text-success">
                    Cliente prescrito correctamente. El equipo comercial lo revisará en breve.
                  </p>
                </div>
              )}

              {formStatus === 'error' && formError && (
                <div className="mt-5 p-4 rounded-xl bg-error/10 text-error flex items-center gap-3">
                  <AlertCircle size={20} />
                  <p className="text-sm text-error">{formError}</p>
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full justify-center mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando prescripción...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Prescribir cliente
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CRMPage;


